import { CalendarRange, Swords, Users } from 'lucide-react'
import { type LucideIcon } from 'lucide-react'

import { SectionLabel } from '@/features/landing/components/SectionLabel'
import { cn } from '@/lib/utils'

interface Step {
  index: string
  title: string
  body: string
  icon: LucideIcon
  /** Tiny secondary cue under the title — the verb you actually perform. */
  verb: string
}

/**
 * "What do you actually do in this thing?" answered in three beats.
 * Sits right after the hero so any visitor who reads "command centre"
 * and wants concrete proof gets it in the next scroll-stop.
 *
 * Copy rules applied (per the writing principles):
 *  - Each step opens with an imperative verb (Form / Schedule / Run)
 *  - Body sentences are concrete actions, not adjectives
 *  - No "powerful" / "easy" / "game-changing" fluff
 *  - Each beat names features visitors can verify exist later in the page
 */
const STEPS: Step[] = [
  {
    index: '01',
    title: 'Form your squad',
    verb: 'Roster · roles · verification',
    body: 'Start a squadron or join one. Hand out roles, verify members, decide who sees what. Your roster, your ranks, your rules — finally in one place.',
    icon: Users,
  },
  {
    index: '02',
    title: 'Schedule the play',
    verb: 'Sessions · meetups · tournaments',
    body: 'Drop a session for tonight. Plan the weekend meetup. Set up a full tournament when it’s time. Every event binds to a Discord channel, so your squad gets pinged where they already are.',
    icon: CalendarRange,
  },
  {
    index: '03',
    title: 'Run it live',
    verb: 'Play-now queues · sign-ups · tournaments · streams',
    body: 'A play-now queue fills for tonight’s session. Sign-ups open for the meetup. The tournament sets who plays who, with match reporting and owner overrides. When a squadmate goes live on Twitch, their stream shows up for everyone.',
    icon: Swords,
  },
]

export function HowItWorks() {
  return (
    <section
      id="how"
      className="relative border-b border-border/60 px-6 py-24 md:py-32"
      aria-labelledby="how-it-works-heading"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 flex flex-col items-start gap-6 md:mb-16">
          <SectionLabel index="01" label="How it works" />
          <h2
            id="how-it-works-heading"
            className="font-display max-w-3xl text-4xl leading-[1.05] font-semibold text-balance md:text-6xl"
          >
            Three steps and your squad’s{' '}
            <span className="gradient-text">up and running</span>.
          </h2>
        </div>

        <ol className="relative grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {/* Connecting rail — only on md+ where the steps line up
              horizontally. Pure decoration, dotted to read as flow not
              divider. */}
          <span
            aria-hidden
            className="pointer-events-none absolute top-[3.25rem] left-12 right-12 hidden h-px bg-gradient-to-r from-transparent via-border to-transparent md:block"
          />
          {STEPS.map((step) => (
            <StepCard key={step.index} step={step} />
          ))}
        </ol>
      </div>
    </section>
  )
}

function StepCard({ step }: { step: Step }) {
  const Icon = step.icon
  return (
    <li
      className={cn(
        'group relative flex flex-col gap-4 rounded-xl border border-border bg-surface/80 p-6 transition-colors duration-150',
        'hover:border-fg-subtle/50 hover:bg-surface',
      )}
    >
      {/* Top row: numbered badge + icon chip. The number ladders the
          three cards as a sequence, the icon hits the verb. */}
      <div className="flex items-center justify-between">
        <span
          aria-hidden
          className="font-display flex h-8 items-center rounded-md border border-primary/30 bg-primary/10 px-2 text-xs font-bold tracking-[0.2em] text-primary tabular-nums"
        >
          // {step.index}
        </span>
        <span
          aria-hidden
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-bg/60"
        >
          <Icon className="size-5 text-primary" strokeWidth={1.75} />
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="font-display text-xl font-semibold tracking-tight text-fg text-balance">
          {step.title}
        </h3>
        <p className="font-display text-[10px] tracking-[0.2em] text-fg-subtle uppercase">
          {step.verb}
        </p>
        <p className="mt-2 text-sm text-fg-muted text-pretty">{step.body}</p>
      </div>

      {/* Tactical corner detail — matches the rest of the landing. */}
      <span
        aria-hidden
        className="pointer-events-none absolute right-3 bottom-3 h-2 w-2 border-r border-b border-fg-subtle/30"
      />
    </li>
  )
}
